// Import style
import './style.scss';

// Import utilities
import { onReady } from '@automator/utilities';

/**
 * Initializes the ButtonDropdowns class when the DOM is ready.
 */
onReady( () => {
	new ButtonDropdowns();
} );

/**
 * Class representing the button dropdown functionality.
 */
export default class ButtonDropdowns {
	/**
	 * Creates an instance of ButtonDropdowns.
	 */
	constructor() {
		// Create dropdowns
		this.createDropdowns();
	}

	/**
	 * Creates dropdown instances for each dropdown element found in the DOM.
	 */
	createDropdowns() {
		// Get elements
		[ ...this.$dropdowns ].forEach( ( $dropdown ) => {
			// Create a new dropdown
			new ButtonDropdown( $dropdown );
		} );
	}

	/**
	 * Gets all dropdown elements in the DOM.
	 *
	 * @return {NodeList} - A NodeList of dropdown elements.
	 */
	get $dropdowns() {
		return document.querySelectorAll( '.uap-dropdown' );
	}
}

/**
 * Class representing a single button dropdown.
 */
class ButtonDropdown {
	/**
	 * Creates an instance of ButtonDropdown.
	 *
	 * @param {HTMLElement} $dropdown - The dropdown element.
	 */
	constructor( $dropdown ) {
		this.$dropdown = $dropdown;

		// Listen clicks to toggle open/close
		this.listenClicks();
	}

	/**
	 * Gets the toggle button element within the dropdown.
	 *
	 * @return {HTMLElement} - The toggle button element.
	 */
	get $toggleButton() {
		return this.$dropdown.querySelector( '.uap-dropdown-toggle' );
	}

	/**
	 * Gets the elements within the dropdown.
	 *
	 * @param {HTMLElement} dropdown - The dropdown element.
	 */
	getElements( dropdown ) {
		this.$elements = {
			dropdown: dropdown,
			toggleBtn: dropdown.querySelector( '.uap-dropdown-toggle' ),
		};
	}

	/**
	 * Adds an event listener to the toggle button to handle click events.
	 */
	listenClicks() {
		// Listen clicks
		this.$toggleButton.addEventListener( 'click', () => {
			// Toggle visibility
			this.toggleMenuVisibility();
		} );
	}

	/**
	 * Toggles the visibility of the dropdown menu.
	 */
	toggleMenuVisibility() {
		// Check if it's open
		if ( this.isOpen ) {
			// Close it
			this.closeMenu();
		} else {
			// Open it
			this.openMenu();
		}
	}

	/**
	 * Opens the dropdown menu and sets up a click listener to close it when clicking outside.
	 */
	openMenu() {
		this.$dropdown.classList.add( 'uap-dropdown--open' );

		/**
		 * Listen clicks outside
		 *
		 * @param {Event} event The event
		 */
		const listenClicksOutside = ( event ) => {
			// If the click happened outside, close the menu
			if ( ! event.target.closest( '.uap-dropdown' ) ) {
				// Remove event listener
				document.removeEventListener( 'click', listenClicksOutside );

				// Close it
				this.closeMenu();
			}
		};

		// Listen clicks everywhere
		document.addEventListener( 'click', listenClicksOutside, false );
	}

	/**
	 * Closes the dropdown menu.
	 */
	closeMenu() {
		this.$dropdown.classList.remove( 'uap-dropdown--open' );
	}

	/**
	 * Checks if the dropdown menu is currently open.
	 *
	 * @return {Boolean} - True if the dropdown menu is open, otherwise false.
	 */
	get isOpen() {
		return this.$dropdown.classList.contains( 'uap-dropdown--open' );
	}
}
